import { FC, ReactNode, useCallback, useEffect, useState } from "react";

import { CCM_ONLY_SECTION_FIELDS, CSV_TEMPLATES, SECTION_HEADERS_WITH_CONTEXT } from "./constants";
import { formatBulkImportError, uploadToS3 } from "./helpers";

import { serverGetBulkImportSignedUrl, serverStartBulkImportJob } from "api/api-server-actions";
import { TermMenu } from "components/menus/TermMenu";
import { BulkImportType } from "components/server-types";
import { Trans, t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Checkbox } from "mds/components/Checkbox";
import { FileUploadField } from "mds/components/FileUploadField";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { PaperClipLinkIcon, RefreshIcon, TrashDeleteIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  courseSelectors,
  selectBulkImportJobById,
  selectCurrentOrg,
  termSelectors,
} from "store/selectors";
import { selectSectionsByTermAndCourse } from "store/selectors/section";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { downloadCSVFile } from "utils/file";
import { BULK_IMPORT_GUIDE_URL } from "utils/urls";
import { fetchAndStoreUserInviteStatuses } from "utils/user-invite-status";
import { isCCMEnabled } from "utils/waffle-flags";

interface BulkImportContentProps {
  importType: BulkImportType;
  onClose: () => void;
}

export const BulkImportContent: FC<BulkImportContentProps> = ({ importType, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTermId, setSelectedTermId] = useState<string>("");
  const [currentImportJobId, setCurrentImportJobId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const currentJob = useAppSelector((state) =>
    currentImportJobId ? selectBulkImportJobById(state, currentImportJobId) : null,
  );
  const courses = useAppSelector(courseSelectors.selectAll);
  const terms = useAppSelector(termSelectors.selectAll);
  const org = useAppSelector(selectCurrentOrg);
  const ccmEnabled = isCCMEnabled();
  const orgName = org?.name;

  const [requireSystemCheck, setRequireSystemCheck] = useState(false);

  const sectionsForSelectedTerm = useAppSelector((s) =>
    selectSectionsByTermAndCourse(s, selectedTermId, ""),
  );

  const dispatch = useAppDispatch();
  const refreshInviteStatuses = useCallback(async () => {
    await fetchAndStoreUserInviteStatuses(dispatch);
  }, [dispatch]);

  const downloadTemplate = useCallback(
    (type: BulkImportType) => {
      const defaultHeaders: (string | number)[] = Array.from(CSV_TEMPLATES[type]);
      let rows: (string | number)[][] = [];

      const headers =
        type === "sections" && !ccmEnabled
          ? defaultHeaders.filter((header) => !CCM_ONLY_SECTION_FIELDS.includes(header as string))
          : defaultHeaders;

      if (type === "sections" && selectedTermId) {
        const selectedTerm = terms.find((term) => term.id === selectedTermId);
        if (selectedTerm) {
          rows = sectionsForSelectedTerm.map((section) => {
            const course = courses.find((cc) => cc.id === section.course_id);
            const templateSection = sectionsForSelectedTerm.find(
              (s) => s.id === section.template_id,
            );

            const row = [
              selectedTerm.title,
              course?.code || "",
              t(`section.kinds.${section.kind}`),
              section.title,
              section.external_section_id || "",
              templateSection?.title || "",
              "",
            ];

            if (!ccmEnabled) {
              return row.filter(
                (_, idx) => !CCM_ONLY_SECTION_FIELDS.includes(defaultHeaders[idx] as string),
              );
            }

            return row;
          });
        }
      }

      const csvData = {
        headers:
          type === "sections"
            ? headers.map(
                (header) =>
                  SECTION_HEADERS_WITH_CONTEXT[header as keyof typeof SECTION_HEADERS_WITH_CONTEXT],
              )
            : defaultHeaders,
        rows,
      };

      downloadCSVFile(csvData, `${type}_template.csv`);
    },
    [selectedTermId, sectionsForSelectedTerm, courses, terms, ccmEnabled],
  );

  useEffect(() => {
    if (currentJob) {
      if (currentJob.status === "completed") {
        const recordsProcessed = currentJob.records_processed || 0;

        setSuccessMessage(
          t(`home.settings.bulk_import_modal.${importType}_import_success`, {
            recordsProcessed,
            orgName,
          }),
        );

        setCurrentImportJobId(null);
        setIsUploading(false);

        trackEvent("Bulk enrollment completed", GENERAL_EVENT_CATEGORY);

        if (importType === "users") {
          refreshInviteStatuses();
        }
      } else if (currentJob.status === "error") {
        const jobErrorMessage = currentJob.error_message || t("error.toasts.unknown");
        const recordsProcessed = currentJob.records_processed || 0;

        if (recordsProcessed > 0) {
          setSuccessMessage(
            t(`home.settings.bulk_import_modal.${importType}_import_success`, {
              recordsProcessed: currentJob.records_processed,
              orgName,
            }),
          );
        }

        setErrorMessage(formatBulkImportError(jobErrorMessage));
        setCurrentImportJobId(null);
        setIsUploading(false);
      }
    }
  }, [currentJob, importType, orgName, refreshInviteStatuses]);

  const handleFileDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setErrorMessage(null);
      setSuccessMessage(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const { upload_url, s3_key } = await serverGetBulkImportSignedUrl(
        importType,
        selectedFile.name,
      );

      await uploadToS3(selectedFile, upload_url);

      const { import_job_id } = await serverStartBulkImportJob(
        importType,
        s3_key,
        selectedFile.name,
        importType === "users" ? requireSystemCheck : false,
      );
      setCurrentImportJobId(import_job_id);
    } catch (error) {
      console.error(`Import ${importType} error:`, error);
      setErrorMessage(t("home.settings.bulk_import_modal.error_starting_import_job"));
      setIsUploading(false);
    }
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setErrorMessage(null);
    setSuccessMessage(null);
    setCurrentImportJobId(null);
    setIsUploading(false);
  };

  const isProcessing = Boolean(isUploading || currentJob);
  const isSuccess = Boolean(successMessage && !errorMessage);

  return (
    <div>
      <div className="flex flex-col gap-6">
        {importType === "sections" && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-black-tint-20">
              {t("home.settings.bulk_import_modal.term_label")}
            </span>

            <TermMenu
              defaultToAll={false}
              menuType="menu"
              selectedTermId={selectedTermId}
              showIcons={false}
              size="s"
              onTermChange={setSelectedTermId}
            />
          </div>
        )}

        <div>
          <Trans
            components={[
              <a
                aria-label="Download template"
                className="text-blue-shade-20 underline"
                href="#"
                key="template-link"
                onClick={(e) => {
                  e.preventDefault();
                  downloadTemplate(importType);
                }}
              />,
            ]}
            i18nKey={`home.settings.bulk_import_modal.import_descriptions.${importType}`}
            t={t}
          />
        </div>

        {importType === "users" && (
          <Checkbox
            checked={requireSystemCheck}
            id="require-system-check"
            onChange={(checked) => setRequireSystemCheck(checked)}
          >
            {t("home.settings.bulk_import_modal.require_system_check_option")}
          </Checkbox>
        )}

        {!isSuccess &&
          (selectedFile ? (
            <div className="flex w-full items-center justify-between rounded-sm bg-black-tint-97 py-2 pl-4 pr-2">
              <div className="flex items-center gap-2">
                <PaperClipLinkIcon className="text-black-tint-40" />
                <span className="font-medium">{selectedFile.name}</span>
              </div>

              <SquareIconButton
                disabled={isProcessing}
                kind="tertiary"
                size="s"
                onClick={handleDeleteFile}
              >
                <TrashDeleteIcon />
              </SquareIconButton>
            </div>
          ) : (
            <FileUploadField
              accept="text/csv"
              isLoading={isProcessing}
              size="m"
              onDrop={handleFileDrop}
            />
          ))}

        {successMessage && !errorMessage && <Banner kind="success">{successMessage}</Banner>}

        {errorMessage && (
          <Banner kind="error">
            <div className="flex flex-col gap-2">
              <div>
                {t("home.settings.bulk_import_modal.import_failure_prefix")}
                {errorMessage}
              </div>

              {successMessage || t("home.settings.bulk_import_modal.import_failure_suffix")}
            </div>
          </Banner>
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <a
          className="text-sm text-blue-shade-20 underline"
          href={BULK_IMPORT_GUIDE_URL}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t("common.get_help")}
        </a>

        <div className="flex gap-2">
          {isSuccess ? (
            <>
              <SquareButton kind="tertiary" size="m" onClick={handleReset}>
                {t("home.settings.bulk_import_modal.upload_another_file")}
              </SquareButton>

              <SquareButton kind="accent" size="m" onClick={onClose}>
                {t("common.done")}
              </SquareButton>
            </>
          ) : (
            <>
              <SquareButton disabled={isProcessing} kind="tertiary" size="m" onClick={onClose}>
                {t("common.cancel")}
              </SquareButton>

              <SquareButton
                disabled={!selectedFile || isProcessing}
                kind="accent"
                size="m"
                onClick={handleImport}
              >
                {isProcessing ? (
                  <IconText
                    iconStart={<RefreshIcon className="animate-spin" />}
                    text="Processing..."
                  />
                ) : (
                  t(`home.settings.bulk_import_modal.types.${importType}`)
                )}
              </SquareButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
